import React from 'react';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MultiSelect, MultiSelectOption } from '../../components/shared/MultiSelect';
import { SelectField } from '../../components/shared/SelectField';
import { SelectItem } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { CloudTier } from '../../services/cloudAssetService';

/** Radix Select has no empty option value, so "no tier filter" needs a sentinel. */
const ANY = '__any__';

/**
 * Filter fields the drawer edits — everything the Resources list can narrow by
 * except the ones the page owns directly (free-text search, sort, pagination).
 *
 * `region` and `environmentTier` are free text on the server but arrive as arrays,
 * so a typed value is carried as a single-element array. That keeps them
 * countable by the shared `countActiveFilters`, which drives the Filters badge.
 */
export interface CloudAdvancedFilterState {
    provider?: string[];
    environmentId?: number[];
    resourceTypeId?: number[];
    providerState?: string[];
    lifecycleStatus?: string[];
    departmentId?: number[];
    region?: string[];
    environmentTier?: string[];
}

export const EMPTY_CLOUD_FILTERS: CloudAdvancedFilterState = {};

interface CloudAdvancedFiltersProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** In-progress edits, owned by the page's table-state hook. */
    draft: CloudAdvancedFilterState;
    onDraftChange: (filters: CloudAdvancedFilterState) => void;
    onApply: () => void;
    onClear: () => void;
    providerOptions: MultiSelectOption[];
    environmentOptions: MultiSelectOption[];
    resourceTypeOptions: MultiSelectOption[];
    providerStateOptions: MultiSelectOption[];
    lifecycleStatusOptions: MultiSelectOption[];
    /** Every tier, active or not — see the drawer's tier field. */
    tiers: CloudTier[];
    departmentOptions: MultiSelectOption[];
}

export const CloudAdvancedFilters: React.FC<CloudAdvancedFiltersProps> = ({
    open,
    onOpenChange,
    draft,
    onDraftChange,
    onApply,
    onClear,
    providerOptions,
    environmentOptions,
    resourceTypeOptions,
    providerStateOptions,
    lifecycleStatusOptions,
    tiers,
    departmentOptions,
}) => {
    // The draft lives in the page's table-state hook, which resets it to the
    // applied filters whenever the drawer opens — so editing here never
    // mutates the list until "Apply Filters" commits it.
    const set = <K extends keyof CloudAdvancedFilterState>(
        key: K,
        value: CloudAdvancedFilterState[K],
    ) => {
        onDraftChange({ ...draft, [key]: value });
    };

    const idsToStrings = (ids?: number[]) => (ids ?? []).map(String);
    const stringsToIds = (values: string[]) => (values.length ? values.map(Number) : undefined);
    /** Free-text field ⇄ the single-element array the query expects. */
    const firstOf = (values?: string[]) => values?.[0] ?? '';
    const asList = (value: string) => (value.trim() ? [value.trim()] : undefined);

    const handleApply = () => {
        onApply();
        onOpenChange(false);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="sm:max-w-lg">
                <SheetHeader>
                    <SheetTitle>Advanced Filters</SheetTitle>
                </SheetHeader>

                <ScrollArea className="flex-1 min-h-0">
                    <div className="space-y-6 px-6 py-5">
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold">Provider</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="cfilter-provider">Cloud Provider</Label>
                                <MultiSelect
                                    id="cfilter-provider"
                                    options={providerOptions}
                                    selected={draft.provider ?? []}
                                    onChange={(v) => set('provider', v.length ? v : undefined)}
                                    placeholder="Any provider"
                                    searchPlaceholder="Search providers…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="cfilter-environment-id">Environment</Label>
                                <MultiSelect
                                    id="cfilter-environment-id"
                                    options={environmentOptions}
                                    selected={idsToStrings(draft.environmentId)}
                                    onChange={(v) => set('environmentId', stringsToIds(v))}
                                    placeholder="Any environment"
                                    searchPlaceholder="Search environments…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="cfilter-state">Provider State</Label>
                                <MultiSelect
                                    id="cfilter-state"
                                    options={providerStateOptions}
                                    selected={draft.providerState ?? []}
                                    onChange={(v) => set('providerState', v.length ? v : undefined)}
                                    placeholder="Any state"
                                    searchPlaceholder="Search states…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="cfilter-region">Region</Label>
                                <Input
                                    id="cfilter-region"
                                    value={firstOf(draft.region)}
                                    onChange={(e) => set('region', asList(e.target.value))}
                                    placeholder="ap-south-1"
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Classification</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="cfilter-type">Resource Type</Label>
                                <MultiSelect
                                    id="cfilter-type"
                                    options={resourceTypeOptions}
                                    selected={idsToStrings(draft.resourceTypeId)}
                                    onChange={(v) => set('resourceTypeId', stringsToIds(v))}
                                    placeholder="Any resource type"
                                    searchPlaceholder="Search types…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="cfilter-lifecycle">Lifecycle Status</Label>
                                <MultiSelect
                                    id="cfilter-lifecycle"
                                    options={lifecycleStatusOptions}
                                    selected={draft.lifecycleStatus ?? []}
                                    onChange={(v) => set('lifecycleStatus', v.length ? v : undefined)}
                                    placeholder="Any lifecycle status"
                                    searchPlaceholder="Search statuses…"
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Ownership</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="cfilter-department">Department</Label>
                                <MultiSelect
                                    id="cfilter-department"
                                    options={departmentOptions}
                                    selected={idsToStrings(draft.departmentId)}
                                    onChange={(v) => set('departmentId', stringsToIds(v))}
                                    placeholder="Any department"
                                    searchPlaceholder="Search departments…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="cfilter-tier">Environment Tier</Label>
                                {/* Every tier is listed, including retired ones:
                                    filtering reads historical data, so hiding a
                                    retired tier would make its resources
                                    unfindable (User Story 95). */}
                                <SelectField
                                    value={firstOf(draft.environmentTier)}
                                    onValueChange={(value) =>
                                        set('environmentTier', value === ANY ? undefined : asList(value))
                                    }
                                    placeholder="Any tier"
                                >
                                    <SelectItem value={ANY}>Any tier</SelectItem>
                                    {tiers.map((tier) => (
                                        <SelectItem key={tier.id} value={tier.value}>
                                            {tier.label}
                                            {tier.isActive === false ? ' (Inactive)' : ''}
                                        </SelectItem>
                                    ))}
                                </SelectField>
                            </div>
                        </div>
                    </div>
                </ScrollArea>

                <SheetFooter>
                    <Button type="button" variant="outline" onClick={onClear}>
                        Clear All
                    </Button>
                    <Button type="button" onClick={handleApply}>
                        Apply Filters
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

export default CloudAdvancedFilters;
